import { useEffect, useState } from 'react';
import { COGNITO_CONFIG } from './api';

/**
 * Cognito hosted-UI redirect target (/callback).
 *
 * Supports both flows:
 *  - authorization code grant (?code=...) → exchanged for tokens here
 *  - implicit grant (#access_token=...)   → tokens read straight from the hash
 *
 * On success the access token is stored in localStorage and the user is sent to
 * /admin. Admin.js is what enforces the session on every visit.
 */
function Callback() {
  const [status, setStatus] = useState('Processing login…');
  const [error, setError] = useState(null);

  useEffect(() => {
    // expiresIn drives isTokenExpired(), so use what Cognito actually returned —
    // a hardcoded hour against a shorter-lived token means writes 401 while the
    // UI still believes the session is good.
    const storeTokens = (accessToken, idToken, expiresIn) => {
      localStorage.setItem('accessToken', accessToken);
      if (idToken) localStorage.setItem('idToken', idToken);
      localStorage.setItem('tokenType', 'Bearer');
      localStorage.setItem('tokenTimestamp', Date.now().toString());
      localStorage.setItem('expiresIn', String(expiresIn || 3600));
      localStorage.setItem('isAdmin', 'true');
    };

    const redirectToAdmin = () => {
      setTimeout(() => {
        window.location.href = `${window.location.origin}/admin`;
      }, 800);
    };

    const exchangeCodeForTokens = async (code) => {
      const tokenEndpoint = `${COGNITO_CONFIG.DOMAIN}/oauth2/token`;
      const params = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: COGNITO_CONFIG.CLIENT_ID,
        code,
        redirect_uri: COGNITO_CONFIG.REDIRECT_URI || `${window.location.origin}/callback`,
      });

      // If a PKCE code_verifier was generated earlier, include it in the
      // token request. initiateLoginWithPKCE stores it in localStorage.
      try {
        const verifier = localStorage.getItem('pkce_code_verifier');
        if (verifier) params.append('code_verifier', verifier);
      } catch (err) {
        // ignore localStorage errors
      }

      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params,
      });

      if (!response.ok) {
        throw new Error(`Token exchange failed: ${response.status} - ${await response.text()}`);
      }
      return response.json();
    };

    const processLogin = async () => {
      // Implicit flow — tokens arrive in the URL hash.
      const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
      const accessTokenFromHash = hashParams.get('access_token');
      if (accessTokenFromHash) {
        storeTokens(accessTokenFromHash, hashParams.get('id_token'), hashParams.get('expires_in'));
        setStatus('Signed in. Redirecting…');
        redirectToAdmin();
        return;
      }

      // Authorization code flow.
      const code = new URLSearchParams(window.location.search).get('code');
      if (code) {
        setStatus('Exchanging code for tokens…');
        try {
          const tokens = await exchangeCodeForTokens(code);
          if (!tokens || !tokens.access_token) throw new Error('No access_token in response');
          storeTokens(tokens.access_token, tokens.id_token, tokens.expires_in);
          try {
            localStorage.removeItem('pkce_code_verifier');
          } catch (err) {
            // ignore
          }
          setStatus('Signed in. Redirecting…');
          redirectToAdmin();
        } catch (err) {
          setError(err.message);
          setStatus('Token exchange failed.');
        }
        return;
      }

      const cognitoError = new URLSearchParams(window.location.search).get('error_description');
      setStatus('No authentication data found');
      setError(cognitoError || 'No code or token in the callback URL.');
    };

    processLogin();
  }, []);

  return (
    <div className="callback-page">
      <h2>{status}</h2>

      {error && (
        <div className="callback-error">
          <p><strong>Error:</strong> {error}</p>
          <p>
            If this is a CORS error, the Cognito app client may have a client secret enabled —
            either disable it, or switch to the implicit flow. If it is a redirect_mismatch, add{' '}
            <code>{COGNITO_CONFIG.REDIRECT_URI}</code> to the app client’s allowed callback URLs.
          </p>
        </div>
      )}

      <p>
        <a className="btn btn-primary" href="/admin">Go to Admin Panel</a>
      </p>
    </div>
  );
}

export default Callback;
